import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <p className="fw-semibold mb-1">{title}</p>
      <div style={{ fontSize: "0.9rem" }}>{children}</div>
    </div>
  );
}

function GdprModal({ show, onClose }) {
  const { t, i18n } = useTranslation();
  const modalRef = useRef(null);
  const isDE = i18n.language?.startsWith("de");

  useEffect(() => {
    if (!show) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    setTimeout(() => modalRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKey);
    };
  }, [show, onClose]);

  if (!show) return null;

  const content = isDE ? (
    <>
      <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
        Die in ezentry gespeicherten Daten werden vom Betreiber der Anlage verwaltet.
        Die ezentry OG stellt die technische Plattform bereit und verarbeitet Daten
        ausschließlich im Auftrag des Betreibers.
      </p>
      <Section title="Verarbeitete Daten">
        <ul className="mb-0 ps-3">
          <li><strong>Kennzeichen</strong> – zur Zugangskontrolle an Schranken</li>
          <li><strong>Benutzerkonten</strong> – Name und E-Mail-Adresse von Personen mit Zugang zum System</li>
          <li><strong>Anmerkungen zu Kennzeichen</strong> – optionale Freitext-Felder; können personenbezogene Daten enthalten, sofern vom Betreiber eingetragen</li>
          <li><strong>Zufahrtsprotokolle</strong> – Zeitstempel und Ergebnis (erlaubt / nicht erlaubt) von Schrankenöffnungen</li>
        </ul>
      </Section>
      <Section title="Zweck & Rechtsgrundlage">
        <p className="mb-1">
          Die Daten werden ausschließlich zur Verwaltung und Steuerung des Zugangs zu
          Parkflächen verarbeitet. Rechtsgrundlage ist in der Regel Art. 6 Abs. 1 lit. b
          DSGVO (Vertragserfüllung) oder Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
          des Betreibers an der Absicherung seiner Flächen).
        </p>
      </Section>
      <Section title="Speicherdauer">
        <p className="mb-1">
          Kennzeichen und Benutzerkonten werden gespeichert, solange der Zugang aktiv ist,
          und nach Beendigung der Berechtigung gelöscht. Zufahrtsprotokolle werden für einen
          begrenzten Zeitraum zur Auswertung aufbewahrt.
        </p>
      </Section>
      <Section title="Ihre Rechte">
        <p className="mb-1">
          Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der
          Verarbeitung sowie das Recht auf Datenübertragbarkeit (Art. 15–20 DSGVO). Außerdem
          steht Ihnen ein Widerspruchsrecht gemäß Art. 21 DSGVO zu. Wenden Sie sich hierfür
          an den Betreiber der Anlage. Darüber hinaus haben Sie das Recht, sich bei der
          zuständigen Aufsichtsbehörde zu beschweren.
        </p>
      </Section>
    </>
  ) : (
    <>
      <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
        Data stored in ezentry is managed by the facility operator.
        ezentry OG provides the technical platform and processes data exclusively
        on behalf of the operator.
      </p>
      <Section title="Data We Process">
        <ul className="mb-0 ps-3">
          <li><strong>License plates</strong> – used for access control at barriers</li>
          <li><strong>User accounts</strong> – name and email address of people with system access</li>
          <li><strong>Plate notes</strong> – optional free-text fields; may contain personal data if entered by the operator</li>
          <li><strong>Access logs</strong> – timestamp and result (allowed / denied) of barrier events</li>
        </ul>
      </Section>
      <Section title="Purpose & Legal Basis">
        <p className="mb-1">
          Data is processed exclusively to manage and control access to parking areas. The
          legal basis is typically Art. 6(1)(b) GDPR (performance of a contract) or
          Art. 6(1)(f) GDPR (legitimate interest of the operator in securing their premises).
        </p>
      </Section>
      <Section title="Retention">
        <p className="mb-1">
          License plates and user accounts are retained for as long as access is active and
          deleted once authorisation ends. Access logs are kept for a limited period for
          review purposes.
        </p>
      </Section>
      <Section title="Your Rights">
        <p className="mb-1">
          You have the right to access, rectification, erasure, restriction of processing,
          and data portability (Art. 15–20 GDPR), as well as the right to object under
          Art. 21 GDPR. Please direct these requests to the facility operator. You also have
          the right to lodge a complaint with the competent supervisory authority.
        </p>
      </Section>
    </>
  );

  return createPortal(
    <>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 2000 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content" ref={modalRef} tabIndex={-1}>
            <div className="modal-header">
              <h5 className="modal-title">{t("gdpr_title")}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {content}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn ez-button-full" onClick={onClose}>
                {t("whats_new_close")}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
    </>,
    document.body
  );
}

export default GdprModal;
